import { isTourMode } from 'tour/tourMode';

/**
 * The row height a list last settled on, kept per list so its next loading skeleton can draw rows of
 * that height instead of a fixed one.
 *
 * The value written is the row virtualizer's self-tuned estimate — the average of the rows it has
 * measured — so it is only ever a height the list's real rows produced. It lives in localStorage under
 * one key for every list, keyed by the list's `columnConfigId`, the same id its column layout persists
 * under. It stays out of that layout entry because consumers key on the entry's content, so every
 * re-tune written into it would re-run their width, visibility and order syncs; "Reset columns" clears
 * both. Storage can be absent or full (private mode, quota): every read and write is guarded, and a
 * list with nothing stored falls back to {@link SKELETON_FALLBACK_ROW_HEIGHT}.
 */

const STORAGE_KEY = 'list-row-heights';

/**
 * The skeleton row a list draws before it has ever measured its own rows: a 40px placeholder inside the
 * cell's 16px top and bottom padding and its 1px bottom border.
 */
export const SKELETON_FALLBACK_ROW_HEIGHT = 73;

const isHeight = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0;

function readStore(): Record<string, unknown> {
    try {
        const parsed: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}');
        return parsed !== null && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
        return {};
    }
}

/** The height to draw this list's skeleton rows at. */
export function readSkeletonRowHeight(listId: string | undefined): number {
    const stored = listId ? readStore()[listId] : undefined;
    return isHeight(stored) ? stored : SKELETON_FALLBACK_ROW_HEIGHT;
}

/** Records the row height this list's rows measured, for its next skeleton. */
export function rememberRowHeight(listId: string | undefined, height: number): void {
    if (!listId || !isHeight(height) || isTourMode()) return;
    const store = readStore();
    if (store[listId] === height) return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...store, [listId]: height }));
    } catch {
        // Storage full or unavailable: the next skeleton falls back to the default height.
    }
}

/** Drops this list's remembered height, so its next skeleton falls back until its rows measure again. */
export function forgetRowHeight(listId: string | undefined): void {
    if (!listId || isTourMode()) return;
    const { [listId]: _removed, ...rest } = readStore();
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
    } catch {
        // Storage unavailable: there is nothing stored to forget.
    }
}
